import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SKIP_FORBIDDEN_REDIRECT } from 'app/shared/interceptors/http-context-tokens';
import { ProgramDashboardService } from 'app/shared/services/api-generated/api/programDashboard.service';
import { ProgramDashboardToken } from 'app/shared/services/api-generated/model/programDashboardToken';
import { LocalStorageService } from 'app/shared/services/app/local-storage.service';
import { defer, Observable, of, ReplaySubject } from 'rxjs';
import { filter, shareReplay, switchMap, tap } from 'rxjs/operators';


// A Tableau browser session is shared across the tab, not scoped to a component,
// which is why this state lives in a service.
//
// Pinned deliberately: the pod's floating `3.latest` is a silent upgrade path
// into a control partners are looking at.
const EMBEDDING_API_URL = 'https://embedding.tableauusercontent.com/tableau.embedding.3.16.0.min.js';

// Logout must not be hostage to Tableau. Past this the sign-out is abandoned and
// logout proceeds, leaving the viewer's Tableau cookie possibly alive, which
// beats a Miradi logout that never finishes.
const SIGN_OUT_DEADLINE_MS = 3000;


@Injectable({
  providedIn: 'root'
})
export class IntegratedDashboardSessionService {

  // Who Tableau believes is signed in, which is a different question, and a
  // different lifetime, from which page context is open.
  private browserSessionUser: string;
  // Incremented by every teardown. A response carrying a stale epoch is
  // discarded: without this, navigating program A to B can paint A's dashboard
  // into B's page.
  private contextEpoch = 0;
  private contextKey: string;
  // One mint in flight per key, shared. Completed mints are not kept, so every
  // load is a fresh permission answer.
  private mintsInFlight: { [key: string]: Observable<ProgramDashboardToken> } = {};
  private moduleLoad: Observable<void>;
  // A subject rather than a cold Observable so the request and its deadline
  // survive the last subscriber leaving.
  private pendingSignOut: Observable<void>;
  private scriptElement: HTMLScriptElement;

  // No AuthService here: taking the identifier as a parameter is what lets
  // AuthService call signOut() at logout without the two files importing each
  // other.
  constructor(
    private localStorageService: LocalStorageService,
    private programDashboardService: ProgramDashboardService,
  ) {
  }

  // Deployment-constant, and never cleared. A viewer who opens a dashboard in
  // program A, moves to B and logs out still has A's Tableau session in this
  // browser, so a teardown that dropped the URL would disarm logout.
  //
  // In local storage rather than in this instance because a dashboard opens in a
  // tab of its own: the Tableau session belongs to the whole browser, but only
  // the minting tab learns the URL that ends it, and that tab is usually not the
  // one the reader logs out from, and may be closed by then. The URL is the
  // deployment's sign-out endpoint; it carries no token and names no user.
  private get signOutUrl(): string {
    return this.localStorageService.getIntegratedDashboardSignOutUrl();
  }

  private set signOutUrl(url: string) {
    // A response without one would otherwise store the word "undefined" and
    // leave every later logout posting to it.
    if (url) this.localStorageService.setIntegratedDashboardSignOutUrl(url);
  }

  // Embedding API v3 is an ES6 module and the build ships no global scripts.
  loadEmbeddingApi(): Observable<void> {
    if (!this.moduleLoad) {
      this.moduleLoad = new Observable<void>(observer => {
        this.scriptElement = document.createElement('script');
        this.scriptElement.type = 'module';
        this.scriptElement.src = EMBEDDING_API_URL;
        this.scriptElement.onload = () => {
          observer.next();
          observer.complete();
        };
        this.scriptElement.onerror = () => {
          // A cached failure would disable dashboards for the rest of the page
          // load over one network blip.
          this.scriptElement?.remove();
          this.scriptElement = undefined;
          this.moduleLoad = undefined;
          observer.error(new Error('Tableau embedding module failed to load'));
        };
        document.head.appendChild(this.scriptElement);
      })
      .pipe(shareReplay(1));
    }

    return this.moduleLoad;
  }

  // No refresh timer: the token authenticates the handshake only, and an open
  // viz is unaffected when it expires.
  //
  // The teardown is awaited rather than fired alongside, or a user switch
  // overlaps two viewers' Tableau sessions in one browser.
  getToken(userIdentifier: string, programIdentifier: string, key: string): Observable<ProgramDashboardToken> {
    return defer(() => this.enterContext(userIdentifier, programIdentifier))
    .pipe(switchMap(() => this.mint(userIdentifier, programIdentifier, key)));
  }

  endPageLifecycle(): void {
    this.clear();
  }

  // Called at logout and at user switch. Completes when Tableau has answered or
  // failed, so a caller about to navigate away can wait for it: the request is
  // cross-site and credentialed, and `keepalive` is not proven to carry one of
  // those.
  signOut(): Observable<void> {
    // One transition, however many callers.
    if (this.pendingSignOut) return this.pendingSignOut;

    const url = this.signOutUrl;
    this.browserSessionUser = undefined;
    this.clear();

    if (!url) return of(undefined);

    // The request and its deadline belong to the transition, not to whoever
    // happens to be subscribed. Unsubscribing during a logout used to cancel the
    // timer and leave the fetch running without a bound.
    const settled = new ReplaySubject<void>(1);
    this.pendingSignOut = settled.asObservable();

    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), SIGN_OUT_DEADLINE_MS);
    // Every outcome completes: answered, refused, or past the deadline.
    const settle = () => {
      clearTimeout(deadline);
      this.pendingSignOut = undefined;
      settled.next();
      settled.complete();
    };

    fetch(url, {
      body: JSON.stringify({}),
      // The session being ended is the browser's cookie for Tableau's origin.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      method: 'POST',
      signal: controller.signal,
    })
    .then(settle, settle);

    return this.pendingSignOut;
  }

  // Per-context state only. A program switch has no reason to end the viewer's
  // Tableau session, so the sign-out URL is not dropped here.
  private clear(): void {
    this.contextEpoch++;
    this.contextKey = undefined;
    this.mintsInFlight = {};
  }

  private enterContext(userIdentifier: string, programIdentifier: string): Observable<void> {
    // A transition in flight must be waited out before anything is decided: the
    // context it is tearing down is still half there.
    if (this.pendingSignOut) {
      return this.pendingSignOut
      .pipe(switchMap(() => this.enterContext(userIdentifier, programIdentifier)));
    }

    const contextKey = `${userIdentifier}|${programIdentifier}`;
    if (this.contextKey === contextKey) return of(undefined);

    // Asked of the Tableau session, not the page context: ending a page
    // lifecycle drops contextKey but leaves a real session open, and a user
    // switch after that must still sign the previous viewer out.
    const isUserSwitch = !!this.browserSessionUser && this.browserSessionUser !== userIdentifier;
    const teardown = isUserSwitch ? this.signOut() : defer(() => { this.clear(); return of(undefined); });

    return teardown.pipe(tap(() => this.contextKey = contextKey));
  }

  private mint(userIdentifier: string, programIdentifier: string, key: string): Observable<ProgramDashboardToken> {
    // Per view: `vizUrl` is per view while the token is not, so a key that
    // ignored the dashboard would let one view's response supply another's URL.
    const inFlightKey = `${this.contextKey}|${key}`;
    if (this.mintsInFlight[inFlightKey]) return this.mintsInFlight[inFlightKey];

    const epoch = this.contextEpoch;
    const forget = () => delete this.mintsInFlight[inFlightKey];

    // A refusal is shown as the viewer's own denied state, not as a redirect.
    const mint = this.programDashboardService.createProgramDashboardToken(programIdentifier, { key: key }, 'body', false, {
      context: new HttpContext().set(SKIP_FORBIDDEN_REDIRECT, true),
    })
    .pipe(
      tap({
        next: (token: ProgramDashboardToken) => {
          if (epoch !== this.contextEpoch) return;
          this.signOutUrl = token.signOutUrl;
          // A mint is the event that creates the Tableau session.
          this.browserSessionUser = userIdentifier;
        },
        complete: forget,
        error: forget,
      }),
      filter(() => epoch === this.contextEpoch),
      shareReplay(1),
    );

    this.mintsInFlight[inFlightKey] = mint;

    return mint;
  }

}
